import Button from '@material-ui/core/Button'
import Dialog from '@material-ui/core/Dialog'
import DialogContent from '@material-ui/core/DialogContent'
import DialogContentText from '@material-ui/core/DialogContentText'
import DialogTitle from '@material-ui/core/DialogTitle'
import _ from 'lodash'
import get from 'lodash/get'
import set from 'lodash/set'
import { useCallback, useState } from 'react'
import {
  Edit,
  required,
  SaveButton,
  TextInput,
  Toolbar,
  useDataProvider,
  useNotify,
  useRedirect,
} from 'react-admin'
import { Form } from 'react-final-form'

import { captureExceptionIfProduction, sanitizeNonNullableStringFields } from 'helpers/utils'
import { upload } from '../../api/brokers'
import { BrokerStatus, REASONS_REQUIRED_BROKER_STATUSES } from '../Brokers/BrokerStatusTransitions'
import { BrokerForm, failureCallback, successCallback } from './BrokerForm'

//This protects against the case where the user erases the value of the field and submits.
const sanitizeValues = (newValues: any) => {
  const nonNullableStringFields = [
    'bol_contact_email',
    'carrier_portal_link',
    'notes',
    'submission_requirement_notes',
    'support_after_hours_email',
    'support_after_hours_phone',
    'support_main_email',
    'support_main_phone',
    'booking_contact_email',
    'booking_contact_phone',
    'billing_address.line2',
    'status',
    'is_carrier_setup_complete',
    'factoring_invoicing_email',
    'factoring_noa_email',
    'pay_status_inquiry_email',
  ]
  const nonNullableNumberFields = [
    'detention_rate_per_hour',
    'detention_start_time_hours',
    'layover_rate_per_day',
    'tonu_rate',
  ]

  sanitizeNonNullableStringFields(newValues, nonNullableStringFields)

  for (let numValue of nonNullableNumberFields) {
    if (get(newValues, numValue) === null) {
      set(newValues, numValue, 0)
    }
  }

  return newValues
}

const BrokerEditSaveButton = (props: any) => {
  const dataProvider = useDataProvider()
  const redirectTo = useRedirect()
  const notify = useNotify()
  const { basePath, record } = props
  const [openStatusConfirm, setOpenStatusConfirm] = useState(false)
  const [status, setStatus] = useState(undefined)
  const [statusReason, setStatusReason] = useState(undefined)
  const [inputValues, setInputValues] = useState(record)
  const [disableSave, setDisableSave] = useState(false)

  const handleChange = (event: any) => {
    setStatusReason(event.target.value)
  }

  const saveBroker = useCallback(
    (inputValues, statusReason) => {
      var updatedValues = sanitizeValues(inputValues)
      if (
        updatedValues.support_main_phone.length > 0 &&
        updatedValues.support_main_phone.slice(0, 2) !== '+1'
      ) {
        updatedValues.support_main_phone = '+1' + updatedValues.support_main_phone
      }
      if (
        updatedValues.support_after_hours_phone.length > 0 &&
        updatedValues.support_after_hours_phone.slice(0, 2) !== '+1'
      ) {
        updatedValues.support_after_hours_phone = '+1' + updatedValues.support_after_hours_phone
      }
      if (
        updatedValues.booking_contact_phone.length > 0 &&
        updatedValues.booking_contact_phone.slice(0, 2) !== '+1'
      ) {
        updatedValues.booking_contact_phone = '+1' + updatedValues.booking_contact_phone
      }
      if (
        updatedValues.carrier_agreement !== null &&
        updatedValues.carrier_agreement !== record.carrier_agreement
      ) {
        const formData = new FormData()
        formData.append('carrier_agreement', updatedValues.carrier_agreement.rawFile)
        if (!_.isUndefined(statusReason)) {
          updatedValues.status_reason = statusReason
        }
        delete updatedValues.carrier_agreement
        upload(record.id, formData).then(() => {
          dataProvider
            .update('brokers', { id: updatedValues.id, data: updatedValues, previousData: record })
            .then(({ data }) => {
              setDisableSave(false)
              successCallback({ data, notify, redirectTo, basePath })
            })
            .catch((error) => {
              setDisableSave(false)
              failureCallback({ error, notify })
              captureExceptionIfProduction(error)
            })
        })
      } else {
        if (updatedValues.carrier_agreement) {
          delete updatedValues.carrier_agreement
        }
        if (!_.isUndefined(statusReason)) {
          updatedValues.status_reason = statusReason
        }
        dataProvider
          .update('brokers', { id: updatedValues.id, data: updatedValues, previousData: record })
          .then(({ data }) => {
            setDisableSave(false)
            successCallback({ data, notify, redirectTo, basePath })
          })
          .catch((error) => {
            setDisableSave(false)
            failureCallback({ error, notify })
            captureExceptionIfProduction(error)
          })
      }
    },
    [dataProvider, notify, redirectTo, basePath, record]
  )

  const handleSave = (values: any) => {
    setDisableSave(true)
    setStatus(values.status)
    if (values.status !== record.status && REASONS_REQUIRED_BROKER_STATUSES.includes(values.status)) {
      setInputValues(values)
      // Check if a status is changed to suspended or disqualified
      setOpenStatusConfirm(true)
    } else {
      saveBroker(values, '')
    }
  }

  return (
    <div>
      <SaveButton {...props} onSave={handleSave} disabled={disableSave} />
      <Dialog open={openStatusConfirm} onClose={() => setOpenStatusConfirm(false)}>
        <DialogTitle>Changing the broker status to "{status}".</DialogTitle>
        <Form
          onSubmit={saveBroker}
          render={(formProps) => {
            const formState = formProps.form.getState()
            const disableSave = _.isEmpty(formState.values.status_reason)
            return (
              <>
                <DialogContent>
                  <DialogContentText>
                    <b>Virtual Carrier customers</b> won't be able to haul a load from "{status}" broker.
                    <br />
                    {status !== BrokerStatus.DNU.id && (
                      <>
                        For <b>Flex and Cash customers</b>, any loads from "{status}" broker will be
                        ineligible for instant pay.
                      </>
                    )}
                  </DialogContentText>
                  <TextInput
                    label="Status reason"
                    validate={required()}
                    source="status_reason"
                    multiline
                    fullWidth
                    rows={4}
                    placeholder="Enter reason"
                    onChange={handleChange}
                  />
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={disableSave}
                    onClick={() => {
                      saveBroker(inputValues, statusReason)
                      setOpenStatusConfirm(false)
                    }}
                  >
                    Change Status
                  </Button>
                </DialogContent>
              </>
            )
          }}
        />
      </Dialog>
    </div>
  )
}

const BrokerEditToolbar = (props: any) => (
  <Toolbar {...props}>
    <BrokerEditSaveButton {...props} />
  </Toolbar>
)

const BrokerEdit = (props: any) => {
  return (
    <Edit {...props}>
      <BrokerForm toolbar={<BrokerEditToolbar />} />
    </Edit>
  )
}

export default BrokerEdit
